/**
 * Component: RecommendationCard
 * SpecReferenceID: BROWSE-V1
 * Requirements: BROWSE-V1-R07
 *
 * Description: Renders one `recommendationContent[]` entry with
 *              inline colour coding. The colours come directly
 *              from the upstream payload (`strength.colour`,
 *              `evidenceStrength.colour`) — no CONFIG-V1 colour
 *              map lookup, no per-deployment translation.
 *
 *              The payload colours are applied via CSS custom
 *              properties (`--rec-strength-colour`,
 *              `--rec-evidence-colour`) set in `style={{ ... }}`
 *              so we can reference them from className-based
 *              utilities without generating dynamic class names
 *              (which Tailwind JIT would strip at build time).
 *              The badge background / border colours use
 *              `var(--rec-*-colour)` through inline style.
 *
 *              `recommendationColourOption` is the upstream mode
 *              selector ("1" | "2" | etc). It flows through as a
 *              `data-rec-colour-mode` attribute so project CSS
 *              (or future design-token work) can scope tweaks per
 *              mode without touching this component.
 *
 *              recommendationText is sanitised via the shared
 *              SafeHtml wrapper — same DOMPurify allow-list used
 *              by SectionContent and the rest of the app.
 *
 * Risk Links: R7
 * FMEA Links: FM7
 */

import React from 'react';
import { SafeHtml } from '@/components/SafeHtml';
import type { RecommendationContentEntry } from '../types';

interface RecommendationCardProps {
  recommendation: RecommendationContentEntry;
  /**
   * Mode selector from `GuidelineDetail.fullContent.recommendationColourOption`,
   * forwarded verbatim to a `data-rec-colour-mode` attribute so CSS
   * can scope tweaks per deployment without touching this file.
   * `null` when the upstream payload omits the field.
   */
  colourOption: string | null;
}

export function RecommendationCard({
  recommendation,
  colourOption,
}: RecommendationCardProps): React.ReactElement {
  const strengthStyle = {
    // CSS custom properties carry the payload colour into inline style
    // without generating dynamic Tailwind class names.
    '--rec-strength-colour': recommendation.strength.colour,
    '--rec-evidence-colour': recommendation.evidenceStrength.colour,
    // Apply the strength colour as a left accent border so the card
    // is recognisable at a glance in long guideline bodies.
    borderLeftColor: 'var(--rec-strength-colour)',
  } as React.CSSProperties;

  return (
    <article
      data-testid={`recommendation-${recommendation.recommendationId}`}
      data-rec-colour-mode={colourOption ?? undefined}
      style={strengthStyle}
      className="rounded-md border border-border border-l-4 bg-card p-4 shadow-sm"
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span
          data-testid={`recommendation-${recommendation.recommendationId}-strength`}
          style={{
            backgroundColor: 'var(--rec-strength-colour)',
            color: 'var(--strength-text-colour, #fff)',
          }}
          className="rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide"
        >
          {recommendation.strength.label}
        </span>
        <span
          data-testid={`recommendation-${recommendation.recommendationId}-evidence`}
          style={{
            backgroundColor: 'var(--rec-evidence-colour)',
            color: 'var(--evidence-text-colour, #fff)',
          }}
          className="rounded px-2 py-0.5 text-xs font-semibold uppercase tracking-wide"
        >
          {recommendation.evidenceStrength.label}
        </span>
      </div>
      {recommendation.recommendationText && (
        <SafeHtml
          html={recommendation.recommendationText}
          className="prose prose-sm max-w-none text-foreground"
        />
      )}
    </article>
  );
}
